// Pure page-window math for `Pagination`, kept out of the component file so it
// can be unit-tested without pulling the gluestack render chain into jest.
export const ELLIPSIS = '…'

/** `pageCell` geometry (`min-w-10` + the row's `gap-x-6`) used to fit cells to the row. */
export const PAGE_CELL_WIDTH = 40
export const PAGE_CELL_GAP = 24
const MIN_VISIBLE_BUTTONS = 2

/**
 * How many `pageCell`s fit the space the nav cells leave over. The caller
 * derives `available` from the row width minus the measured nav cells, so it
 * does not depend on the cell count — the result cannot oscillate. Until the
 * first measurement it yields the minimum, so an unmeasured row under-fills
 * instead of overflowing.
 */
export const fittingButtons = (available: number, maxButtons: number): number => {
    if (available <= 0) {
        return Math.min(MIN_VISIBLE_BUTTONS, maxButtons)
    }
    const fits = Math.floor((available + PAGE_CELL_GAP) / (PAGE_CELL_WIDTH + PAGE_CELL_GAP))
    return Math.max(MIN_VISIBLE_BUTTONS, Math.min(maxButtons, fits))
}

export type PaginationItem =
    | { type: 'page'; page: number; label: string; active: boolean }
    | { type: 'ellipsis'; page: number; key: string }

/**
 * Windowed page model with first/last anchors + ellipses — ported from the
 * legacy `aos-ui` Pagination so behaviour (and the jump targets) is identical.
 */
/**
 * The window anchors the first and last page, so at low cell counts every
 * remaining slot can fall outside the current page and the row renders with no
 * active cell at all. Swapping the middle slot for the current page keeps the
 * "you are here" pill in every configuration — including the two-cell row a
 * narrow panel produces, where the current page has to displace one of the two
 * anchors rather than sit between them. It takes the first one, so the jump to
 * the last page survives. Counts of 6+ already contain it and are left
 * untouched.
 */
const ensureCurrentPage = (items: PaginationItem[], current: number): PaginationItem[] => {
    if (items.length === 0 || items.some(item => item.type === 'page' && item.active)) {
        return items
    }
    const middle = items.length > 2 ? Math.floor(items.length / 2) : 0
    return items.map((item, index) =>
        index === middle
            ? { type: 'page', page: current, label: `${current + 1}`, active: true }
            : item,
    )
}

export const buildItems = (
    current: number,
    totalPages: number,
    maxButtons: number,
): PaginationItem[] => {
    const totalButtons = Math.min(maxButtons, totalPages)

    const window = (min: number, max: number, buffer: number): [number, number] => {
        if (max - min <= buffer) {
            return [min, max]
        }
        if (current + buffer / 2 > max) {
            return [max - buffer, max]
        }
        if (current - buffer / 2 < min) {
            return [min, min + buffer]
        }
        const start = Math.floor(current - buffer / 2)
        return [start, start + buffer]
    }

    const [left, right] = window(0, totalPages, totalButtons)
    const pages: number[] = []
    for (let p = left; p < right; p++) {
        pages.push(p)
    }

    const items = pages.map((p, index): PaginationItem => {
        if (index === 0) {
            return { type: 'page', page: 0, label: '1', active: current === 0 }
        }
        if (index === pages.length - 1) {
            return {
                type: 'page',
                page: totalPages - 1,
                label: `${totalPages}`,
                active: current === totalPages - 1,
            }
        }
        if ((index === 1 && p > 1) || (index === pages.length - 2 && p < totalPages - 2)) {
            return { type: 'ellipsis', page: p, key: `ellipsis-${index}` }
        }
        return { type: 'page', page: p, label: `${p + 1}`, active: p === current }
    })

    return ensureCurrentPage(items, current)
}
